import { useCallback, useState } from "react";

import { useConnectCall } from "../../../hooks/useConnectCall";
import { useUpsertNotes } from "../../../hooks/useUpsertNotes";
import tokenUtils from "../../../utils/tokenUtils";
import { getCallIdFromResponse } from "../utils/studentDetailsHelpers";
import useCallNotesModal from "./useCallNotesModal";
import useSnackbarState from "./useSnackbarState";

const useStudentInfoActions = ({ studentData, verticalInfo }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const {
    notesModal,
    openNotesModal,
    closeNotesModal,
    setNotesText,
  } = useCallNotesModal();
  const {
    errorSnackbar,
    successSnackbar,
    showError,
    showSuccess,
    closeError,
    closeSuccess,
  } = useSnackbarState();

  const {
    mutate: connectCall,
    isPending: isConnectCallPending,
    isLoading: isConnectCallLoading,
  } = useConnectCall();
  const {
    mutate: upsertNotes,
    isPending: isUpsertNotesPending,
    isLoading: isUpsertNotesLoading,
  } = useUpsertNotes();

  const isCalling = isConnectCallPending || isConnectCallLoading;
  const isUpsertingNotes = isUpsertNotesPending || isUpsertNotesLoading;

  const handleConnectCall = useCallback(() => {
    const callerId = tokenUtils.getUserId();
    setConfirmOpen(false);

    connectCall(
      {
        caller_id: String(callerId),
        callee_id: String(studentData?.uam_id),
        callee_vertical_id: String(verticalInfo?.id || ""),
      },
      {
        onSuccess: (res) => {
          const status = res?.status || res?.data?.status;
          if (status !== "SUCCESS") {
            showError("Failed to connect call.");
            return;
          }

          showSuccess("Call connected successfully.");
          openNotesModal(getCallIdFromResponse(res));
        },
        onError: () => showError("Failed to connect call."),
      },
    );
  }, [connectCall, openNotesModal, showError, showSuccess, studentData?.uam_id, verticalInfo?.id]);

  const handleUpsertNotes = useCallback(() => {
    upsertNotes(
      { CallId: notesModal.callId, notes: notesModal.text },
      {
        onSuccess: () => {
          showSuccess("Notes added successfully.");
          closeNotesModal();
        },
        onError: () => showError("Failed to add notes."),
      },
    );
  }, [
    closeNotesModal,
    notesModal.callId,
    notesModal.text,
    showError,
    showSuccess,
    upsertNotes,
  ]);

  return {
    confirmOpen,
    setConfirmOpen,
    notesModal,
    closeNotesModal,
    setNotesText,
    errorSnackbar,
    successSnackbar,
    closeError,
    closeSuccess,
    isCalling,
    isUpsertingNotes,
    handleConnectCall,
    handleUpsertNotes,
  };
};

export default useStudentInfoActions;
